import { useState } from 'react';
import { Alert, Box, Button, Card, Skeleton, Stack, Typography } from '@mui/material';
import HowToRegRounded from '@mui/icons-material/HowToRegRounded';
import { EmptyState } from '@/components/EmptyState';
import { UserAvatar } from '@/components/UserAvatar';
import { useConfirm } from '@/components/feedback/confirmContext';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useAsync } from '@/hooks/useAsync';
import { approveRequest, listApprovals, rejectRequest } from '@/platform/api';
import { useSession } from '@/platform/session';
import { ROLE_LABEL, type Person } from '@/platform/types';
import { formatDateTime, formatRelative } from '@/utils/format';
import { PageHeader } from '../PageHeader';
import { PeopleTabs } from '../people/PeopleTabs';
import { AdminTabs } from './AdminPage';

/**
 * Teachers who confirmed a teacher address and wait to be let in. Admins see every request;
 * coordinators those of the institution chosen in the top bar.
 */
export function ApprovalsPage() {
  const requests = useAsync(listApprovals);
  const { refresh, isAdmin } = useSession();
  const notify = useNotify();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);

  const answer = async (person: Person, approve: boolean) => {
    const name = person.name ?? person.email;
    const role = person.requestedRole ? ROLE_LABEL[person.requestedRole].toLowerCase() : '';
    if (!approve) {
      const ok = await confirm({
        title: `Recusar ${name}?`,
        description: `A conta fica desativada e a pessoa não consegue entrar. Se mudar de ideia, reative em Pessoas.`,
        confirmLabel: 'Recusar',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(person.id);
    try {
      await (approve ? approveRequest(person.id) : rejectRequest(person.id));
      requests.setData((list) => (list ?? []).filter((p) => p.id !== person.id));
      void refresh(); // badge count
      notify(
        approve ? `${name} aprovado(a) como ${role}.` : `Pedido de ${name} recusado.`,
        'success',
      );
    } catch {
      notify('Não foi possível responder ao pedido.', 'error');
      requests.reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title={isAdmin ? 'Administração' : 'Pessoas'}
        subtitle="Quem confirmou um e-mail de professor só entra depois da sua aprovação."
      />
      {isAdmin ? <AdminTabs /> : <PeopleTabs />}
      {requests.error ? (
        <Alert severity="error">Não foi possível carregar os pedidos.</Alert>
      ) : requests.loading ? (
        <Card>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ m: 2 }} />
          ))}
        </Card>
      ) : !requests.data?.length ? (
        <Card>
          <EmptyState
            icon={HowToRegRounded}
            title="Nenhum pedido aguardando"
            description="Quando alguém confirmar um e-mail de professor, o pedido aparece aqui e você recebe um e-mail."
          />
        </Card>
      ) : (
        <Card>
          {requests.data.map((person) => (
            <Stack
              key={person.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2 }}
              sx={(t) => ({
                px: { xs: 2, sm: 2.5 },
                py: 2,
                alignItems: { sm: 'center' },
                '& + &': { borderTop: `1px solid ${t.vars.palette.divider}` },
              })}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}
              >
                <UserAvatar
                  user={{ ...person, signInMethod: 'unknown' }}
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {person.name ?? '—'}
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {person.institutionalEmail}
                    {person.institutions.length > 0 &&
                      ` · ${person.institutions.map((i) => i.name).join(', ')}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Google: {person.email} · pediu acesso como{' '}
                    {person.requestedRole ? ROLE_LABEL[person.requestedRole].toLowerCase() : '—'}{' '}
                    <span title={formatDateTime(person.createdAt)}>
                      · cadastro {formatRelative(person.createdAt)}
                    </span>
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', flexShrink: 0 }}>
                <Button
                  color="inherit"
                  disabled={busy === person.id}
                  onClick={() => void answer(person, false)}
                >
                  Recusar
                </Button>
                <Button
                  variant="contained"
                  disabled={busy === person.id}
                  onClick={() => void answer(person, true)}
                >
                  Aprovar
                </Button>
              </Stack>
            </Stack>
          ))}
        </Card>
      )}
    </>
  );
}
